import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Loader } from 'src/components';
import TabsPageLayout from 'src/components/TabsPageLayout';
import { RESULTS_TAB_DATA_COUNTS_KEY, WDAC_APP_QUERY_KEY } from 'src/constants/query';
import { pagesText } from 'src/constants/pagesText';
import { PORTAL_ROUTE } from 'src/constants/routes';
import { DEFAULT_ENVIRONMENT_ID } from 'src/constants/storage';
import useSubscribeStorage from 'src/hooks/useSubscribeStorage';
import {
  invalidateWin32AppImportQueries,
  postGlobalAppStatus,
  statusUpdateMovesImports,
} from 'src/hooks/wdac';
import { wdacAppTabs } from 'src/pages/wdac-results/constants';
import WDACResultsService from 'src/services/wdac-results.service';
import { APPLICATION_TRUST_STATUS } from 'src/utils/enums/applicationTrustStatus';
import parseError from 'src/utils/parseError';
import { WDAC_APP_PREVIOUS_PATH } from '../constants';
import Win32AppStatusModal, { getStatusSavedMessage } from '../Win32AppStatusModal';

// The telemetry tab opens the form with its import scope and a callback for once it saves; the
// general tab can open it straight on the artifacts step.
const CLOSED_STATUS_MODAL = {
  open: false,
  importScope: null,
  startOnArtifacts: false,
  onSaved: null,
};

const WDACAppTabs = () => {
  const { appId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const [statusModal, setStatusModal] = useState(CLOSED_STATUS_MODAL);
  const closeStatusModal = () => setStatusModal(previous => ({ ...previous, open: false }));

  const appQueryKey = useMemo(
    () => [WDAC_APP_QUERY_KEY, appId, customerEnvId],
    [appId, customerEnvId],
  );

  const { isLoading: appIsLoading, data: appData } = useQuery(
    appQueryKey,
    () => WDACResultsService.getWDACApp({ customerEnvId, globalApplicationVersionId: appId }),
    { enabled: !!appId && !!customerEnvId },
  );
  const appLoading = appIsLoading || !customerEnvId;

  const { data: counts, isLoading: countsIsLoading } = useQuery(
    [RESULTS_TAB_DATA_COUNTS_KEY, appId],
    () =>
      WDACResultsService.getAppWdacTabsDataCounts({
        globalApplicationVersionId: appId,
        customerEnvId,
      }),
    { enabled: !!appId && !!customerEnvId },
  );

  const { mutate: updateStatus, isLoading: statusLoading } = useMutation(postGlobalAppStatus, {
    onError: error => {
      enqueueSnackbar(parseError(error) || pagesText.somethingWentWrong, {
        variant: 'error',
        autoHideDuration: 3000,
      });
    },
    // Writing through the cache rather than into local state keeps the header and the general
    // tab, which reads the same query, showing the same thing.
    onSuccess: (data, variables) => {
      queryClient.setQueryData(appQueryKey, previous =>
        previous
          ? {
              ...previous,
              status: data?.status,
              notes: data?.notes,
              trustUpdatedOn: data?.trustUpdatedOn,
            }
          : previous,
      );
      // A status change recalculates the app's aggregates, which the tab counts read.
      queryClient.invalidateQueries([RESULTS_TAB_DATA_COUNTS_KEY]);
      if (statusUpdateMovesImports(variables)) {
        invalidateWin32AppImportQueries(queryClient);
      }

      enqueueSnackbar(getStatusSavedMessage(variables), {
        variant: 'success',
        autoHideDuration: 3000,
      });
      statusModal.onSaved?.();
      closeStatusModal();
      // Trusting builds what the general tab shows, so that is where a trust finishes.
      if (variables.status === APPLICATION_TRUST_STATUS.Trusted) {
        navigate(`${PORTAL_ROUTE}/wdac-apps/${appId}/general`);
      }
    },
  });

  const handleSave = values =>
    updateStatus({ customerEnvId, globalApplicationVersionId: appId, ...values });

  const openEditModal = useCallback(
    ({ importScope = null, startOnArtifacts = false, onSaved = null } = {}) =>
      setStatusModal({ open: true, importScope, startOnArtifacts, onSaved }),
    [],
  );
  const outletContext = useMemo(() => ({ onEditStatus: openEditModal }), [openEditModal]);

  const backNavigationPath = useMemo(() => {
    const storedPath = sessionStorage.getItem(WDAC_APP_PREVIOUS_PATH);
    return storedPath || `${PORTAL_ROUTE}/wdac-results/win32-apps`;
  }, []);

  if (appLoading) {
    return <Loader />;
  }

  return (
    <div>
      <TabsPageLayout
        hasBackButton
        // Status and the catalog are acted on from the general tab rather than the header.
        title={
          <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
            {appData?.name || ''}
            {appData?.version && (
              <Box component="span" sx={{ color: '#cdf3ceff', fontSize: '0.9em', fontWeight: 400 }}>
                ({appData.version})
              </Box>
            )}
          </Box>
        }
        tabs={wdacAppTabs(appId, counts, countsIsLoading || !customerEnvId)}
        backNavigationPath={backNavigationPath}
        wrapperProps={{ sx: { maxWidth: '100%' } }}
        titleProps={{ fontSize: '1.4rem' }}
        outletContext={outletContext}
      />
      <Win32AppStatusModal
        open={statusModal.open}
        onClose={closeStatusModal}
        importScope={statusModal.importScope}
        startOnArtifacts={statusModal.startOnArtifacts}
        globalApplicationVersionId={appId}
        summary={[appData?.name, appData?.version].filter(Boolean).join(' ')}
        currentStatus={appData?.status}
        currentDate={appData?.trustUpdatedOn}
        onSave={handleSave}
        loading={statusLoading}
      />
    </div>
  );
};

export default WDACAppTabs;
